import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { BusinessHours, DayKey } from "@dropin/shared/schemas";
import { dayKeys, socialPlatforms } from "@dropin/shared/schemas";
import { getPublicBrief, type PublicBriefResponse } from "../api";

const DAY_LABELS: Record<DayKey, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export function BriefPage() {
  const { businessId = "" } = useParams();
  const [data, setData] = useState<PublicBriefResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    getPublicBrief(businessId)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "failed"));
  }, [businessId]);

  if (error) {
    return (
      <div className="card max-w-md">
        <h1 className="text-xl font-semibold tracking-tightish">Brief not available</h1>
        <p className="mt-1 text-sm text-muted">
          This business hasn't published a creator brief yet.
        </p>
        <Link to="/dashboard" className="btn mt-4 inline-flex" data-testid="brief-back-link">
          Back to dashboard
        </Link>
      </div>
    );
  }
  if (!data) return <div className="card text-muted animate-fade-in">Loading…</div>;

  const { business, brief, perks, viewerIsVerifiedCreator } = data;
  const initial = (business.name?.[0] ?? "?").toUpperCase();
  const mapsHref = business.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${business.address}${business.city ? ", " + business.city : ""}`,
      )}`
    : business.lat != null && business.lon != null
      ? `https://www.google.com/maps/search/?api=1&query=${business.lat},${business.lon}`
      : null;
  const social = business.socialLinks ?? {};
  const socialEntries = socialPlatforms
    .map((p) => ({ p, handle: social[p] }))
    .filter((s): s is { p: typeof socialPlatforms[number]; handle: string } => !!s.handle);

  return (
    <article className="max-w-3xl mx-auto grid gap-5" data-testid="public-brief">
      {/* Header — logo + name + city + chip */}
      <header className="card">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-20 w-20 rounded-lg border border-border bg-paper overflow-hidden flex items-center justify-center">
            {business.logoUrl ? (
              <img
                src={business.logoUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="text-3xl font-semibold text-muted" aria-hidden>
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <span className="chip-primary text-xs">Creator brief</span>
            <h1 className="mt-2 text-2xl font-semibold tracking-tightish truncate">
              {business.name}
            </h1>
            {business.city && (
              <p className="text-sm text-muted truncate">{business.city}</p>
            )}
            {business.description && (
              <p className="mt-2 text-sm text-muted leading-relaxed">{business.description}</p>
            )}
          </div>
        </div>
      </header>

      {/* Open perks — only for verified creators */}
      {viewerIsVerifiedCreator && perks.length > 0 && (
        <section className="card">
          <h2 className="text-lg font-semibold tracking-tightish">
            Open perks ({perks.length})
          </h2>
          <p className="mt-1 text-sm text-muted">
            Active perks you can claim. The redemption code appears after the business approves your claim.
          </p>
          <div className="mt-4 grid gap-3">
            {perks.map((p) => (
              <div
                key={p.id}
                className="rounded-md border border-border bg-paper px-3 py-3 flex items-start gap-3"
                data-testid={`brief-perk-${p.id}`}
              >
                {p.imageUrl && (
                  <div className="shrink-0 h-14 w-14 rounded-md border border-border bg-surface overflow-hidden">
                    <img
                      src={p.imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold">{p.title}</p>
                    {p.requireApproval ? (
                      <span className="chip-accent text-[10px] !py-0">Needs approval</span>
                    ) : (
                      <span className="chip-primary text-[10px] !py-0">Instant</span>
                    )}
                  </div>
                  {p.description && (
                    <p className="mt-0.5 text-xs text-muted leading-relaxed">
                      {p.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!viewerIsVerifiedCreator && (
        <section className="card border-accent/30 bg-accent/[0.04]">
          <p className="text-sm">
            <strong>Active perks are hidden.</strong> Sign in as a verified creator
            to see what this business is offering and claim a perk.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <Link to="/signin" className="btn-primary text-sm">
              Sign in
            </Link>
            <Link to="/dashboard" className="btn-ghost text-sm">
              I'm already signed in
            </Link>
          </div>
        </section>
      )}

      {/* Visit details — address, hours, links */}
      <section className="card grid gap-4">
        <h2 className="text-lg font-semibold tracking-tightish">Visit</h2>
        {business.address ? (
          <DetailRow label="Address">
            <a
              href={mapsHref ?? "#"}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm"
              data-testid="brief-maps-link"
            >
              {[business.address, business.city].filter(Boolean).join(", ")} ↗
            </a>
          </DetailRow>
        ) : business.city ? (
          <DetailRow label="City">
            {mapsHref ? (
              <a href={mapsHref} target="_blank" rel="noreferrer noopener" className="text-sm">
                {business.city} ↗
              </a>
            ) : (
              <span className="text-sm">{business.city}</span>
            )}
          </DetailRow>
        ) : null}

        {business.hours && <HoursTable hours={business.hours} />}

        {business.phone && (
          <DetailRow label="Phone">
            <a href={`tel:${business.phone}`} className="text-sm">{business.phone}</a>
          </DetailRow>
        )}
        {business.contactEmail && (
          <DetailRow label="Email">
            <a href={`mailto:${business.contactEmail}`} className="text-sm">
              {business.contactEmail}
            </a>
          </DetailRow>
        )}
        {business.websiteUrl && (
          <DetailRow label="Website">
            <a href={business.websiteUrl} target="_blank" rel="noreferrer noopener" className="text-sm truncate inline-block max-w-full">
              {business.websiteUrl} ↗
            </a>
          </DetailRow>
        )}

        {socialEntries.length > 0 && (
          <DetailRow label="Social">
            <div className="flex flex-wrap gap-1.5">
              {socialEntries.map(({ p, handle }) => (
                <span key={p} className="chip text-xs">
                  {p === "twitter" ? "X" : p.charAt(0).toUpperCase() + p.slice(1)}: {handle}
                </span>
              ))}
            </div>
          </DetailRow>
        )}
      </section>

      {/* The brief content itself — markdown */}
      <section className="card prose-brief">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{brief.content}</ReactMarkdown>
      </section>

      <Link
        to="/dashboard"
        className="btn-ghost text-sm self-start"
        data-testid="brief-back-link"
      >
        ← Back to dashboard
      </Link>
    </article>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[88px_1fr] sm:grid-cols-[110px_1fr] items-baseline gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function HoursTable({ hours }: { hours: BusinessHours }) {
  return (
    <DetailRow label="Hours">
      <ul className="grid gap-1 text-sm">
        {dayKeys.map((d) => {
          const h = hours[d];
          return (
            <li key={d} className="flex justify-between gap-3">
              <span className="text-muted w-12 shrink-0">{DAY_LABELS[d]}</span>
              <span className={h.closed ? "text-muted" : "text-ink"}>
                {h.closed ? "Closed" : `${h.open} – ${h.close}`}
              </span>
            </li>
          );
        })}
      </ul>
    </DetailRow>
  );
}
